// MARK://dhk8/pos/pages/WaitlistPage
// Lane #164 — fila de espera standalone (no más drawer en /floor).
//
// Vista táctil dedicada con:
//   - Código de cola visible grande (Q12) — sirve para preguntar por # de lugar
//   - Botones tap-friendly por entry:
//       ↑ subir prioridad (POST /waitlist/:id/move {dir:'up'})
//       ↓ bajar prioridad (POST /waitlist/:id/move {dir:'down'})
//       ✉ Enviar mensaje (DISABLED globalmente por ahora — Lane #163-ter wire wa.me)
//       ✓ Sentar (POST /waitlist/:id/seat — abre PosTableSession)
//       ⏺⏺⏺ Sin respuesta (POST /waitlist/:id/no-show-attempt, 3 strikes → EXPIRED rojo)
//       ✕ Eliminar (DELETE /waitlist/:id, marca LEFT)
//   - Toggle "Incluir historia" para ver SEATED/LEFT/EXPIRED del día
//   - Estadísticas rápidas (en cola / sentadas hoy / no asistieron / promedio)
//   - Polling 20s
//
// Apunta al MISMO backend que el WaitlistPanel anterior + el admin WaitlistPage.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowUp, ArrowDown, MessageSquare, Check, X, Trash2, RefreshCw, Plus,
  History as HistoryIcon, Users, Clock as ClockIcon, Phone,
} from 'lucide-react'
import { Button, Dialog, Input, Select } from '@dockethub/ui'

import {
  listWaitlist,
  addWaitlistEntry,
  notifyWaitlistEntry,
  moveWaitlistEntry,
  attemptNoShowWaitlistEntry,
  seatWaitlistEntry,
  leaveWaitlistEntry,
  type WaitlistEntryDto,
  type CreateWaitlistInput,
} from '../lib/clients/floor.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useConfirm } from '../components/ConfirmDialog.js'
import { SharePublicLink } from '../features/SharePublicLink.js'

const POLL_MS = 20_000

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}
function minutesSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
}

function statusLabel(s: WaitlistEntryDto['status']): string {
  switch (s) {
    case 'WAITING': return 'Esperando'
    case 'NOTIFIED': return 'Notificada'
    case 'SEATED': return 'Sentada'
    case 'LEFT': return 'Se fue'
    case 'EXPIRED': return 'No asistió'
  }
}
function rowToneClass(s: WaitlistEntryDto['status'], attempts: number): string {
  // EXPIRED = rojo. NOTIFIED con >0 strikes = amarillo creciente. Otros = neutro.
  if (s === 'EXPIRED') return 'is-expired'
  if (s === 'NOTIFIED' && attempts >= 1) return 'is-warning'
  if (s === 'SEATED') return 'is-muted'
  if (s === 'LEFT') return 'is-muted'
  return ''
}

export default function WaitlistPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const confirm = useConfirm()
  const [items, setItems] = useState<WaitlistEntryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [includeHistory, setIncludeHistory] = useState(false)
  const [creating, setCreating] = useState(false)
  const [actingId, setActingId] = useState<string | null>(null)

  async function reload() {
    try {
      setError(null)
      setLoading(true)
      const r = await listWaitlist({ includeHistory })
      // Activas primero (WAITING/NOTIFIED) ordenadas por position,
      // después el resto por addedAt desc (historial).
      const active = r.filter((e) => e.status === 'WAITING' || e.status === 'NOTIFIED')
        .sort((a, b) => a.position - b.position)
      const past = r.filter((e) => e.status !== 'WAITING' && e.status !== 'NOTIFIED')
        .sort((a, b) => b.addedAt.localeCompare(a.addedAt))
      setItems([...active, ...past])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar fila')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    const iv = setInterval(() => void reload(), POLL_MS)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeHistory])

  const activeItems = useMemo(
    () => items.filter((e) => e.status === 'WAITING' || e.status === 'NOTIFIED'),
    [items],
  )
  const stats = useMemo(() => {
    const seated = items.filter((e) => e.status === 'SEATED')
    const expired = items.filter((e) => e.status === 'EXPIRED')
    const left = items.filter((e) => e.status === 'LEFT')
    const totalCovers = activeItems.reduce((s, e) => s + e.partySize, 0)
    const waitTimes = seated
      .filter((e) => e.seatedAt)
      .map((e) => (new Date(e.seatedAt!).getTime() - new Date(e.addedAt).getTime()) / 60_000)
    const avgWaitMin = waitTimes.length > 0
      ? Math.round(waitTimes.reduce((a, b) => a + b, 0) / waitTimes.length)
      : null
    return { activeCount: activeItems.length, totalCovers, seatedCount: seated.length, expiredCount: expired.length, leftCount: left.length, avgWaitMin }
  }, [items, activeItems])

  async function actOn(id: string, fn: () => Promise<unknown>) {
    if (actingId) return
    setActingId(id)
    try {
      await fn()
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    } finally {
      setActingId(null)
    }
  }

  function handleMove(e: WaitlistEntryDto, dir: 'up' | 'down') {
    void actOn(e.id, () => moveWaitlistEntry(e.id, dir))
  }
  function handleAttempt(e: WaitlistEntryDto) {
    // Si es WAITING, primer "no responde" implícitamente lo notifica primero.
    // Backend marca attempts directamente; el cliente puede llamarlo en ambos.
    void actOn(e.id, async () => {
      if (e.status === 'WAITING') await notifyWaitlistEntry(e.id).catch(() => {})
      await attemptNoShowWaitlistEntry(e.id)
    })
  }
  function handleRemove(e: WaitlistEntryDto) {
    void (async () => {
      if (!(await confirm({ title: 'Quitar de la fila', message: `¿Quitar a ${e.name} de la fila?`, confirmLabel: 'Quitar', tone: 'danger' }))) return
      await actOn(e.id, () => leaveWaitlistEntry(e.id))
    })()
  }
  function handleSeat(e: WaitlistEntryDto) {
    // El "Sentar" abre seat-flow. Por simplicidad: redirige a /floor con
    // ?seat=<id>, el FloorPage detecta y prompts pick-a-table.
    // (Reusa el flow existente del WaitlistPanel.)
    navigate(`/floor?seat=waitlist:${encodeURIComponent(e.id)}`)
  }

  const _serverId = user?.id // referenced by handleSeat indirectly via /floor flow

  return (
    <div className="dhk8-waitlist-page">
      <header className="dhk8-waitlist-page__head">
        <div>
          <h1 className="dhk8-waitlist-page__title">Fila de espera</h1>
          <p className="dhk8-waitlist-page__sub">
            Los clientes pueden anotarse desde <strong>fila.nintaigo.com</strong>. Comparte el enlace o el QR. Aquí los gestionas.
          </p>
        </div>
        <div className="dhk8-waitlist-page__actions">
          <SharePublicLink kind="fila" label="Compartir fila" />
          <label className="dhk8-waitlist-page__history">
            <input type="checkbox" checked={includeHistory} onChange={(e) => setIncludeHistory(e.target.checked)} />
            <HistoryIcon size={14} /> Historia
          </label>
          <Button variant="ghost" size="sm" leading={<RefreshCw size={14} />} onClick={() => void reload()}>Recargar</Button>
          <Button variant="primary" size="sm" leading={<Plus size={14} />} onClick={() => setCreating(true)}>Agregar</Button>
        </div>
      </header>

      {/* Stats */}
      <div className="dhk8-waitlist-page__stats">
        <Stat label="En cola"          value={String(stats.activeCount)} hint={`${stats.totalCovers} personas`} />
        <Stat label="Sentadas hoy"     value={String(stats.seatedCount)} />
        <Stat label="No asistieron"    value={String(stats.expiredCount)} tone="expired" />
        <Stat label="Se fueron"        value={String(stats.leftCount)} tone="muted" />
        <Stat label="Espera promedio"  value={stats.avgWaitMin !== null ? `${stats.avgWaitMin} min` : '—'} hint="sentados" />
      </div>

      {error && <div className="dhk8-waitlist-page__err" role="alert">{error}</div>}

      {/* Lista */}
      {loading && items.length === 0 ? (
        <div className="dhk8-waitlist-page__empty">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="dhk8-waitlist-page__empty">{includeHistory ? 'Sin actividad hoy.' : 'No hay nadie en la fila.'}</div>
      ) : (
        <ul className="dhk8-waitlist-list">
          {items.map((e, i) => {
            const isActive = e.status === 'WAITING' || e.status === 'NOTIFIED'
            const canMoveUp = isActive && i > 0 && (items[i - 1]?.status === 'WAITING' || items[i - 1]?.status === 'NOTIFIED')
            const nextActive = items[i + 1]
            const canMoveDown = isActive && !!nextActive && (nextActive.status === 'WAITING' || nextActive.status === 'NOTIFIED')
            const busy = actingId === e.id
            return (
              <li key={e.id} className={`dhk8-waitlist-card ${rowToneClass(e.status, e.noShowAttempts)}`}>
                {/* Code/position + arrow stack */}
                <div className="dhk8-waitlist-card__lead">
                  <div className="dhk8-waitlist-card__code" title={`Posición ${e.position}`}>
                    {e.queueCode ?? `#${e.position}`}
                  </div>
                  {isActive && (
                    <div className="dhk8-waitlist-card__arrows">
                      <button
                        type="button"
                        className="dhk8-waitlist-card__arrow"
                        onClick={() => handleMove(e, 'up')}
                        disabled={busy || !canMoveUp}
                        aria-label="Subir prioridad"
                        title="Subir"
                      ><ArrowUp size={16} /></button>
                      <button
                        type="button"
                        className="dhk8-waitlist-card__arrow"
                        onClick={() => handleMove(e, 'down')}
                        disabled={busy || !canMoveDown}
                        aria-label="Bajar prioridad"
                        title="Bajar"
                      ><ArrowDown size={16} /></button>
                    </div>
                  )}
                </div>

                {/* Info principal */}
                <div className="dhk8-waitlist-card__main">
                  <div className="dhk8-waitlist-card__row1">
                    <strong className="dhk8-waitlist-card__name">{e.name}</strong>
                    <span className={`dhk8-waitlist-card__badge tone-${e.status.toLowerCase()}`}>
                      {statusLabel(e.status)}
                    </span>
                  </div>
                  <div className="dhk8-waitlist-card__meta">
                    <span><Users size={11} /> {e.partySize}</span>
                    <span><ClockIcon size={11} /> {minutesSince(e.addedAt)} min · {fmtTime(e.addedAt)}</span>
                    {e.phone && <span><Phone size={11} /> {e.phone}</span>}
                  </div>
                  {e.notes && <div className="dhk8-waitlist-card__notes">{e.notes}</div>}
                </div>

                {/* Actions */}
                {isActive ? (
                  <div className="dhk8-waitlist-card__actions">
                    {/* Mensaje (DISABLED global por ahora) */}
                    <Button
                      variant="ghost"
                      size="sm"
                      leading={<MessageSquare size={14} />}
                      disabled
                      title="Enviar mensaje (próximamente)"
                      className="dhk8-waitlist-card__icon-btn"
                    >&nbsp;</Button>

                    {/* Sentar */}
                    <Button
                      variant="primary"
                      size="sm"
                      leading={<Check size={14} />}
                      onClick={() => handleSeat(e)}
                      disabled={busy}
                    >Sentar</Button>

                    {/* Sin respuesta — botón con 3 dots */}
                    <NoShowButton
                      attempts={e.noShowAttempts}
                      onClick={() => handleAttempt(e)}
                      disabled={busy || !e.phone}
                      hint={e.phone ? undefined : 'Sin teléfono'}
                    />

                    {/* Eliminar */}
                    <Button
                      variant="danger"
                      size="sm"
                      leading={<X size={14} />}
                      onClick={() => handleRemove(e)}
                      disabled={busy}
                      title="Quitar de fila"
                      className="dhk8-waitlist-card__icon-btn"
                    >&nbsp;</Button>
                  </div>
                ) : (
                  <div className="dhk8-waitlist-card__terminal-meta">
                    {e.status === 'EXPIRED' && (
                      <span title="Marcado tras 3 intentos sin respuesta"><Trash2 size={11} /> No asistió</span>
                    )}
                    {e.status === 'SEATED' && e.seatedAt && (
                      <span><Check size={11} /> Sentada {fmtTime(e.seatedAt)}</span>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {creating && (
        <AddToWaitlistDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void reload() }} />
      )}

      <style>{styles}</style>
    </div>
  )
}

// ── Stat card ───────────────────────────────────────────────────────────────

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'expired' | 'muted' }) {
  return (
    <div className={`dhk8-waitlist-stat${tone ? ` is-${tone}` : ''}`}>
      <span className="dhk8-waitlist-stat__label">{label}</span>
      <span className="dhk8-waitlist-stat__value">{value}</span>
      {hint && <span className="dhk8-waitlist-stat__hint">{hint}</span>}
    </div>
  )
}

// ── NoShowButton (3 strikes) ────────────────────────────────────────────────
//
// Render visual del mockup: │ 3 ⏺ ⏺ ⏺ │
//   - Número = intentos restantes (3 → 2 → 1 → 0)
//   - Dots filled = strikes ya gastados (●○○ → ●●○ → ●●●)
//   - Al 3er tap (attempts=3), el componente queda disabled (status es EXPIRED).

function NoShowButton({ attempts, onClick, disabled, hint }: {
  attempts: number
  onClick: () => void
  disabled?: boolean
  hint?: string
}) {
  const remaining = Math.max(0, 3 - attempts)
  const allUsed = attempts >= 3
  const filled = (i: number) => i < remaining
  return (
    <button
      type="button"
      className={`dhk8-noshow-btn${attempts > 0 ? ' has-attempts' : ''}${allUsed ? ' is-final' : ''}`}
      onClick={onClick}
      disabled={disabled || allUsed}
      title={hint ?? `Sin respuesta (quedan ${remaining})`}
      aria-label={`Sin respuesta. ${attempts} de 3 intentos.`}
    >
      <span className="dhk8-noshow-btn__count">{remaining}</span>
      <span className="dhk8-noshow-btn__dots">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`dhk8-noshow-btn__dot${filled(i) ? ' is-filled' : ''}`} />
        ))}
      </span>
    </button>
  )
}

// ── Add dialog (usa @dockethub/ui — Dialog, Input, Select, Button) ────────────────

function AddToWaitlistDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [partySize, setPartySize] = useState(2)
  const [estimatedWaitMin, setEstimatedWaitMin] = useState<number | ''>('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit() {
    if (!name.trim()) { setErr('Nombre requerido'); return }
    setBusy(true); setErr(null)
    try {
      const input: CreateWaitlistInput = { name: name.trim(), partySize }
      if (phone.trim()) input.phone = phone.trim()
      if (notes.trim()) input.notes = notes.trim()
      if (estimatedWaitMin !== '' && estimatedWaitMin > 0) input.estimatedWaitMin = estimatedWaitMin
      await addWaitlistEntry(input)
      onCreated()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al agregar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title="Agregar a fila"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={busy || !name.trim()}>
            {busy ? 'Guardando…' : 'Agregar'}
          </Button>
        </>
      }
    >
      <label className="dhk8-waitlist-field">
        <span>Nombre</span>
        <Input value={name} onChange={(e) => setName(e.currentTarget.value)} autoFocus placeholder="Como te llamen" />
      </label>

      <div className="dhk8-waitlist-field-row">
        <label className="dhk8-waitlist-field">
          <span>Teléfono (opcional)</span>
          <Input value={phone} onChange={(e) => setPhone(e.currentTarget.value)} placeholder="+52..." />
        </label>
        <label className="dhk8-waitlist-field">
          <span>Personas</span>
          <Select
            value={String(partySize)}
            onChange={(e) => setPartySize(Number(e.currentTarget.value))}
          >
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={String(n)}>{n}</option>
            ))}
          </Select>
        </label>
      </div>

      <label className="dhk8-waitlist-field">
        <span>Espera estimada (min, opcional)</span>
        <Input
          type="number"
          value={String(estimatedWaitMin)}
          onChange={(e) => {
            const v = e.currentTarget.value
            setEstimatedWaitMin(v === '' ? '' : Math.max(0, Number(v) || 0))
          }}
          placeholder="ej. 25"
        />
      </label>

      <label className="dhk8-waitlist-field">
        <span>Notas (opcional)</span>
        <Input value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder="Pidió terraza, walk-in..." />
      </label>

      {err && <div className="dhk8-waitlist-page__err" role="alert">{err}</div>}
    </Dialog>
  )
}

const styles = `
  .dhk8-waitlist-page { display: flex; flex-direction: column; gap: 12px; padding: 12px; }
  .dhk8-waitlist-page__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
  .dhk8-waitlist-page__title { font-size: 20px; font-weight: 600; margin: 0; color: var(--pos-fg, #E8EFE9); font-family: var(--font-display, sans-serif); }
  .dhk8-waitlist-page__sub { font-size: 12px; color: var(--pos-fg-tertiary, #7E8C82); margin: 4px 0 0; }
  .dhk8-waitlist-page__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .dhk8-waitlist-page__history { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; cursor: pointer; color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-waitlist-page__err { padding: 8px 12px; background: rgba(229,72,77,0.15); border: 1px solid var(--pos-danger, #E5484D); color: var(--pos-danger, #E5484D); font-size: 13px; border-radius: var(--pos-radius-md, 8px); }
  .dhk8-waitlist-page__empty { padding: 32px; text-align: center; color: var(--pos-fg-tertiary, #7E8C82); }

  /* ── Stats grid ──────────────────────────────────────────────────────── */
  .dhk8-waitlist-page__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px; }
  .dhk8-waitlist-stat { display: flex; flex-direction: column; gap: 2px; padding: 12px 14px; background: var(--pos-elevated, #1E2520); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); border-radius: var(--pos-radius-md, 8px); }
  .dhk8-waitlist-stat.is-expired { border-color: var(--pos-danger, #E5484D); background: var(--pos-danger-bg, rgba(229,72,77,0.12)); }
  .dhk8-waitlist-stat.is-muted { opacity: 0.7; }
  .dhk8-waitlist-stat__label { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--pos-fg-tertiary, #7E8C82); font-family: var(--font-mono, ui-monospace); }
  .dhk8-waitlist-stat__value { font-size: 26px; font-weight: 600; font-variant-numeric: tabular-nums; font-family: var(--font-mono, ui-monospace); color: var(--pos-fg, #E8EFE9); line-height: 1; padding-top: 2px; }
  .dhk8-waitlist-stat__hint { font-size: 10px; color: var(--pos-fg-tertiary, #7E8C82); }

  /* ── List ────────────────────────────────────────────────────────────── */
  .dhk8-waitlist-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
  .dhk8-waitlist-card {
    display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center;
    padding: 10px 12px;
    background: var(--pos-elevated, #1E2520);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14));
    border-radius: var(--pos-radius-md, 8px);
    transition: background 120ms ease, border-color 120ms ease;
  }
  .dhk8-waitlist-card.is-warning { background: color-mix(in oklch, var(--pos-warning, #E0A23C) 12%, var(--pos-elevated, #1E2520)); border-color: var(--pos-warning, #E0A23C); }
  .dhk8-waitlist-card.is-expired { background: color-mix(in oklch, var(--pos-danger, #E5484D) 14%, var(--pos-elevated, #1E2520)); border-color: var(--pos-danger, #E5484D); opacity: 0.85; }
  .dhk8-waitlist-card.is-muted { opacity: 0.55; }

  .dhk8-waitlist-card__lead { display: flex; flex-direction: column; align-items: center; gap: 4px; min-width: 56px; }
  .dhk8-waitlist-card__code { font-family: var(--font-mono, ui-monospace); font-size: 22px; font-weight: 700; line-height: 1; color: var(--pos-fg, #E8EFE9); letter-spacing: 0.04em; }
  .dhk8-waitlist-card.is-warning .dhk8-waitlist-card__code { color: var(--pos-warning, #E0A23C); }
  .dhk8-waitlist-card.is-expired .dhk8-waitlist-card__code { color: var(--pos-danger, #E5484D); }
  .dhk8-waitlist-card__arrows { display: flex; flex-direction: column; gap: 2px; }
  .dhk8-waitlist-card__arrow { width: 28px; height: 24px; display: inline-flex; align-items: center; justify-content: center; background: transparent; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14)); border-radius: var(--pos-radius-sm, 4px); color: var(--pos-fg-secondary, #B6BFB8); cursor: pointer; transition: background 120ms ease; }
  .dhk8-waitlist-card__arrow:hover:not(:disabled) { background: var(--pos-surface, #161C18); color: var(--pos-fg, #E8EFE9); }
  .dhk8-waitlist-card__arrow:disabled { opacity: 0.25; cursor: not-allowed; }

  .dhk8-waitlist-card__main { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .dhk8-waitlist-card__row1 { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
  .dhk8-waitlist-card__name { font-size: 15px; color: var(--pos-fg, #E8EFE9); }
  .dhk8-waitlist-card__badge { padding: 2px 8px; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; border: 1px solid currentColor; border-radius: var(--pos-radius-full, 999px); font-family: var(--font-mono, ui-monospace); }
  .dhk8-waitlist-card__badge.tone-waiting { color: var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-waitlist-card__badge.tone-notified { color: var(--pos-warning, #E0A23C); }
  .dhk8-waitlist-card__badge.tone-seated { color: var(--pos-success, #2EA043); }
  .dhk8-waitlist-card__badge.tone-left { color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-waitlist-card__badge.tone-expired { color: var(--pos-danger, #E5484D); }
  .dhk8-waitlist-card__meta { display: flex; gap: 12px; font-size: 11px; color: var(--pos-fg-tertiary, #7E8C82); flex-wrap: wrap; }
  .dhk8-waitlist-card__meta span { display: inline-flex; align-items: center; gap: 4px; }
  .dhk8-waitlist-card__notes { font-size: 11px; color: var(--pos-fg-secondary, #B6BFB8); padding: 4px 8px; background: var(--pos-surface, #161C18); border-radius: var(--pos-radius-sm, 4px); display: inline-block; max-width: fit-content; }

  .dhk8-waitlist-card__actions { display: flex; gap: 4px; flex-wrap: wrap; align-items: center; }
  .dhk8-waitlist-card__terminal-meta { font-size: 12px; color: var(--pos-fg-tertiary, #7E8C82); padding: 4px 8px; display: inline-flex; align-items: center; gap: 4px; }

  /* Icon-only buttons in the actions row — match noshow-btn height/padding for visual symmetry */
  .dhk8-waitlist-card__icon-btn {
    min-height: 32px;
    min-width: 32px;
    padding-left: 10px;
    padding-right: 10px;
  }

  /* ── NoShowButton ───────────────────────────────────────────────────── */
  .dhk8-noshow-btn {
    display: inline-flex; align-items: center; gap: 6px;
    min-height: 32px; padding: 4px 10px;
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-warning, #E0A23C);
    border-radius: var(--pos-radius-md, 8px);
    color: var(--pos-warning, #E0A23C);
    font-family: var(--font-mono, ui-monospace);
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: all 120ms ease;
  }
  .dhk8-noshow-btn:hover:not(:disabled) { background: color-mix(in oklch, var(--pos-warning, #E0A23C) 20%, var(--pos-surface, #161C18)); }
  .dhk8-noshow-btn:active:not(:disabled) { transform: scale(0.96); }
  .dhk8-noshow-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .dhk8-noshow-btn.has-attempts { background: color-mix(in oklch, var(--pos-warning, #E0A23C) 15%, var(--pos-surface, #161C18)); }
  .dhk8-noshow-btn.is-final { background: var(--pos-danger, #E5484D); color: var(--pos-fg, #fff); border-color: var(--pos-danger, #E5484D); }
  .dhk8-noshow-btn__count { font-size: 14px; font-weight: 700; min-width: 12px; text-align: center; }
  .dhk8-noshow-btn__dots { display: inline-flex; gap: 3px; align-items: center; }
  .dhk8-noshow-btn__dot { width: 8px; height: 8px; border-radius: 50%; background: transparent; border: 1.5px solid currentColor; transition: background 120ms ease; }
  .dhk8-noshow-btn__dot.is-filled { background: currentColor; }

  /* ── Dialog fields ──────────────────────────────────────────────────── */
  .dhk8-waitlist-field { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: var(--fg-secondary, var(--pos-fg-secondary, #B6BFB8)); margin-bottom: 10px; }
  .dhk8-waitlist-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
`
